import { Injectable, computed, signal } from '@angular/core';

/** The query parameter the API redirects back with when single sign-on fails. */
export const SSO_ERROR_PARAM = 'sso_error';

const GENERIC_SSO_ERROR_MESSAGE =
  "We couldn't complete single sign-on. Please try again.";

// The API redirects back with one fixed-enum code; protocol-level codes share
// one message. A Map rather than an object literal: the code comes from the
// URL, so a key such as `constructor` must not resolve to an inherited property.
const SSO_ERROR_MESSAGES: ReadonlyMap<string, string> = new Map<string, string>(
  [
    ['state', GENERIC_SSO_ERROR_MESSAGE],
    ['nonce', GENERIC_SSO_ERROR_MESSAGE],
    ['signature', GENERIC_SSO_ERROR_MESSAGE],
    ['issuer', GENERIC_SSO_ERROR_MESSAGE],
    ['audience', GENERIC_SSO_ERROR_MESSAGE],
    ['expired', GENERIC_SSO_ERROR_MESSAGE],
    ['replay', GENERIC_SSO_ERROR_MESSAGE],
    ['binding', GENERIC_SSO_ERROR_MESSAGE],
    ['flow_expired', GENERIC_SSO_ERROR_MESSAGE],
    ['subject', GENERIC_SSO_ERROR_MESSAGE],
    [
      'disabled',
      "Single sign-on isn't available for your organization right now. Sign in with your password instead."
    ],
    [
      'domain',
      "Your email address isn't set up for single sign-on. Sign in with your password instead."
    ],
    [
      'no_role',
      "Your single sign-on account doesn't have access to VIVE. Ask your administrator to grant access."
    ],
    [
      'account_conflict',
      "We couldn't match your single sign-on account to a VIVE account. Contact VIVE support."
    ],
    [
      'login_denied',
      "This account can't sign in right now. Contact VIVE support."
    ],
    [
      'name_missing',
      'Your single sign-on account is missing a first or last name. Ask your administrator to add it, then try again.'
    ]
  ]
);

@Injectable({
  providedIn: 'root'
})
export class SsoService {
  private readonly errorCode = signal<string | null>(null);

  /** Approved copy for the `sso_error` this page loaded with; null when absent or unknown. */
  public readonly errorMessage = computed<string | null>(() => {
    const code = this.errorCode();
    return code === null ? null : (SSO_ERROR_MESSAGES.get(code) ?? null);
  });

  /**
   * Runs once, before the router's first navigation. The code always leaves the
   * URL; it is recorded only when `show` is true, so a page that loaded signed
   * in never shows a failure meant for a signed-out one.
   */
  public consumeErrorFromUrl(show: boolean): void {
    const url = new URL(window.location.href);
    const code = url.searchParams.get(SSO_ERROR_PARAM);
    if (code === null) {
      return;
    }

    url.searchParams.delete(SSO_ERROR_PARAM);
    // The absolute URL, never pathname + search: a path starting with `//`
    // would read as another host, replaceState would throw, and a throw in an
    // app initializer stops the app starting.
    window.history.replaceState(window.history.state, '', url.href);
    if (show) {
      this.errorCode.set(code);
    }
  }

  public clearError(): void {
    this.errorCode.set(null);
  }
}
